/**
 * Production sampling profiler via the JS Self-Profiling API (Chromium-only; the HOST
 * page must be served with the `Document-Policy: js-profiling` response header).
 *
 * Opt-in via profilerSampleRate (0..1, default 0): that share of page loads is profiled
 * from init until load+5s (or page hide), then the raw trace ships to
 * /api/profiles/ingest where the collector aggregates top functions by self-time.
 */
import type { ResolvedConfig } from './config.js';
import { uuid } from './util.js';

interface SelfProfiler {
  stop(): Promise<unknown>;
}

type ProfilerCtor = new (opts: { sampleInterval: number; maxBufferSize: number }) => SelfProfiler;

function profilesUrl(collectorUrl: string): string {
  if (/\/api\/events\/ingest\/?$/.test(collectorUrl)) {
    return collectorUrl.replace(/\/api\/events\/ingest\/?$/, '/api/profiles/ingest');
  }
  return `${collectorUrl.replace(/\/$/, '')}/api/profiles/ingest`;
}

export function installProfiler(cfg: ResolvedConfig): () => void {
  if (typeof window === 'undefined' || cfg.profilerSampleRate <= 0) return () => {};
  const Ctor = (window as unknown as { Profiler?: ProfilerCtor }).Profiler;
  if (!Ctor || Math.random() >= cfg.profilerSampleRate) return () => {};

  let profiler: SelfProfiler | null = null;
  const startedAt = performance.now();
  try {
    profiler = new Ctor({ sampleInterval: 10, maxBufferSize: 100_000 });
  } catch {
    // Page is missing `Document-Policy: js-profiling` — profiling unavailable.
    return () => {};
  }

  const finish = async () => {
    const p = profiler;
    profiler = null;
    if (!p) return;
    try {
      const trace = await p.stop();
      const body = JSON.stringify({
        id: uuid(),
        project_id: cfg.projectId,
        environment: cfg.environment,
        route: location.pathname,
        url: location.href.split('?')[0],
        duration_ms: Math.round(performance.now() - startedAt),
        trace,
      });
      void fetch(profilesUrl(cfg.collectorUrl), {
        method: 'POST',
        keepalive: true,
        headers: { 'content-type': 'application/json', 'x-aris-api-key': cfg.apiKey },
        body,
      }).catch(() => {});
      if (cfg.debug) console.info('[aris] profile captured', { route: location.pathname });
    } catch {
      /* never throw into the host app */
    }
  };

  const stopSoonAfterLoad = () => setTimeout(() => void finish(), 5000);
  if (document.readyState === 'complete') stopSoonAfterLoad();
  else window.addEventListener('load', stopSoonAfterLoad, { once: true });
  const onHide = () => {
    if (document.visibilityState === 'hidden') void finish();
  };
  document.addEventListener('visibilitychange', onHide);

  return () => {
    document.removeEventListener('visibilitychange', onHide);
    void finish();
  };
}
